import { useCallback, useMemo } from "react"
import type React from "react"
import { format } from "d3-format"
import { Tooltip, IconButton } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import ListItemDropDown from "components/list-item-dropdown"
import type { ListItemDropDownOption } from "components/list-item-dropdown/types"
import GraphTemplateSelector from "connected-components/graph/components/template-selector"
import PeriodDisplay from "connected-components/dashboard/components/period-display"
import { IDashboardContext } from "connected-components/dashboard/context/types"
import { buildModelCategoryOptions } from "connected-components/graph/helpers"
import { useCategorySync } from "connected-components/graph/hooks/use-category-sync"
import { useFactorSensitivityContext, FactorSensitivityModelMetadata as ModelMetadata } from "./context"
import { DEFAULT_THRESHOLD_PARAMS } from "helpers/threshold"

const formatThresholdDisplay = format(".1%")

interface ListItemDropDownProps {
  label: string
  value: string
  setValue: (id: string) => void
  options: ListItemDropDownOption[]
  alignMenu?: "left" | "right"
  disablePortal?: boolean
  listSubheader?: string
  classes?: object
  disabled?: boolean
  disableSubHeader?: boolean
  disabledTooltip?: string
  listAction?: unknown
}

const TypedListItemDropDown = ListItemDropDown as React.ComponentType<ListItemDropDownProps>

const BASIC_CATEGORY_OPTIONS: ListItemDropDownOption[] = [{ id: "total", primaryText: "All Factors" }]

const PREDICTED_FACTOR_SENSITIVITY_INFO = (
  "Factor sensitivity measures the expected portfolio return for a 1 standard deviation move in each factor "
  + "(monthly horizon). Predicted sensitivity uses current factor loadings."
)

const HISTORICAL_FACTOR_SENSITIVITY_INFO = (
  "Factor sensitivity measures the expected portfolio return for a 1 standard deviation move in each factor "
  + "(monthly horizon). Historical sensitivity uses a 125-day lookback."
)

interface FactorSensitivityHeaderProps {
  modelMetadata: ModelMetadata
  context: IDashboardContext
}

const FACTOR_SENSITIVITY_INFO_BY_TYPE = {
  predicted: PREDICTED_FACTOR_SENSITIVITY_INFO,
  historical: HISTORICAL_FACTOR_SENSITIVITY_INFO,
}

export default function FactorSensitivityHeader({ modelMetadata, context }: FactorSensitivityHeaderProps) {
  const {
    selectedCategory,
    setSelectedCategory,
    sensitivityType,
    isPreferencesLoaded,
  } = useFactorSensitivityContext()

  const categoryOptions = useMemo(
    (): ListItemDropDownOption[] => buildModelCategoryOptions(modelMetadata, BASIC_CATEGORY_OPTIONS),
    [modelMetadata],
  )

  useCategorySync(categoryOptions, selectedCategory, setSelectedCategory, isPreferencesLoaded)

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category)
  }, [setSelectedCategory])

  return (
    <>
      <div className="graph-header-left">
        <GraphTemplateSelector context={context} />
        <PeriodDisplay context={context} isPerformance={false} />
        <TypedListItemDropDown
          label="Category"
          value={selectedCategory}
          setValue={handleCategoryChange}
          options={categoryOptions}
          alignMenu="left"
          disablePortal
        />
      </div>
      <div className="graph-header-right">
        <div className="threshold-label">
          <span className="title">THRESHOLDS</span>
          <span className="contents">{formatThresholdDisplay(DEFAULT_THRESHOLD_PARAMS.value)}</span>
        </div>
        <Tooltip
          title={FACTOR_SENSITIVITY_INFO_BY_TYPE[sensitivityType]}
          placement="bottom-start"
          slotProps={{ tooltip: { className: "factor-sensitivity-info-tooltip" } }}
        >
          <IconButton size="small" aria-label="Factor sensitivity information">
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </>
  )
}
